// Segments API is real (see api.ts) — shapes here mirror the confirmed backend
// contract from the ConvoHub Segments API Postman collection (2026-07-29).

export type ContactScope = "all" | "sms_portal" | "journey_builder" | "crm";

export interface Segment {
  id: string;
  name: string;
  createdOn: string;
  contacts: string; // display string, e.g. "23,000"
  validContacts: number;
  invalidContacts: number;
  usedIn: string; // e.g. "8 Broadcasts"
  sourceScope: ContactScope;
  // Only populated once GET /segments/{id} has been fetched (list rows don't
  // carry it) — used to pre-fill the builder's rules when editing.
  ruleTree?: RuleTree | null;
  // Also only from GET /segments/{id} — the segment's own real matched
  // contacts (page 1), so the builder can show them immediately without an
  // extra round trip right after opening.
  contactRows?: SegmentContactRow[];
  contactsTotalPages?: number;
}

// ── Raw backend shape — confirmed from a real GET /segments/ response
// (2026-07-28): { total, pages, page, limit, data: SegmentApiRecord[], search }.
export interface SegmentApiRecord {
  id: string;
  name: string;
  source_scope?: ContactScope;
  rule_tree?: RuleTree | null;
  created_at?: string;
  contacts?: number;
  valid_contacts?: number;
  invalid_contacts?: number;
  used_in?: number;
  // Only present on GET /segments/{id} (paginated matching contacts).
  results?: SegmentContactRow[];
  total_pages?: number;
  page?: number;
  limit?: number;
  [key: string]: unknown;
}

export const mapRecordToSegment = (record: SegmentApiRecord): Segment => ({
  id: record.id,
  name: record.name,
  createdOn: record.created_at
    ? new Date(record.created_at).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })
    : "—",
  contacts: typeof record.contacts === "number" ? record.contacts.toLocaleString() : "—",
  validContacts: record.valid_contacts ?? 0,
  invalidContacts: record.invalid_contacts ?? 0,
  usedIn: `${record.used_in ?? 0} Broadcast${record.used_in === 1 ? "" : "s"}`,
  sourceScope: record.source_scope || "all",
  ruleTree: record.rule_tree ?? null,
  contactRows: Array.isArray(record.results) ? record.results : undefined,
  contactsTotalPages: record.total_pages,
});

// ── Contact rows — GET /segments/{id}'s paginated `results` (real matched
// contacts for that segment, the only endpoint that returns actual people).
export interface SegmentContactRow {
  phone: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  sources: string[];
  attributes?: Record<string, Record<string, unknown>>;
}

// Mask PII in the segment builder's contact table — this is a preview of who
// a broadcast would reach, not a contact lookup tool, so the full phone/email
// never needs to be on screen. Keeps the first 5 digits of a phone number
// (e.g. "08083******") and the first 4 characters of an email's local part
// (e.g. "jeje****@gmail.com") — enough to recognize a contact you already
// know, not enough to read out cold.
export const maskPhone = (phone?: string | null): string => {
  if (!phone) return "—";
  return phone.length <= 5 ? phone : `${phone.slice(0, 5)}${"*".repeat(phone.length - 5)}`;
};

export const maskEmail = (email?: string | null): string => {
  if (!email) return "—";
  const at = email.indexOf("@");
  if (at <= 0) return email;
  return `${email.slice(0, Math.min(4, at))}****${email.slice(at)}`;
};

// ── Attribute + event catalogs (GET /segments/attributes, GET /segments/events) ──
// Confirmed shape (2026-08-05) — build pickers/operators from these live,
// per-org responses; never hardcode field names or operator lists.
export type AttributeFieldType = "string" | "number" | "date" | "boolean" | "select";

// A "select" field's option — most are plain strings, but network_operator's
// options carry a brand logo instead (see AttributeCatalogField.options).
export interface AttributeSelectOption {
  value: string;
  logo_url: string | null;
}

export interface AttributeCatalogField {
  field: string; // e.g. "attributes.wallet_balance", or a universal "first_name"
  label: string;
  type: AttributeFieldType;
  operators: RuleOperator[];
  // Only present when type === "select" — the exact choices to render as a
  // dropdown. Plain strings for every select field except network_operator,
  // which carries {value, logo_url} objects so the dropdown can show each
  // carrier's brand logo.
  options?: (string | AttributeSelectOption)[];
}

// A group is usually a flat list of fields, but (like "Convo AI" as of
// 2026-09-16) can instead be split further into named subgroups (e.g.
// "Custom", "Behavioral") — same idea as EventCatalogGroup below, for the
// same reason: some sections have enough fields that the backend buckets
// them one level deeper.
export type AttributeCatalogGroup = AttributeCatalogField[] | Record<string, AttributeCatalogField[]>;
export const isNestedAttributeGroup = (group: AttributeCatalogGroup): group is Record<string, AttributeCatalogField[]> =>
  !Array.isArray(group);

// Grouped by section label ("Common Fields", "Telco Attributes", "Convo AI",
// "SMS Portal Attributes", "Journey Builder Attributes", "ConvoCRM
// Attributes", ...) exactly as the backend returns it — render each key as a
// header with its fields (or, for a nested group, its subgroups) under it.
export type AttributeCatalog = Record<string, AttributeCatalogGroup>;

// "Custom Events" items only ever carry `name`; "Broadcast Events" items
// (nested one level deeper by channel — see EventCatalogGroup) carry
// `event_type` + `label` instead (the same 10-outcome catalog per channel).
export interface EventCatalogItem {
  name?: string;
  event_type?: string;
  label?: string;
  description: string;
}

// The real backend key to send when this item becomes a rule's eventType —
// `name` for custom events, `event_type` for broadcast ones.
export const eventItemKey = (item: EventCatalogItem): string => item.event_type || item.name || "";
// What to show the user — `label` (broadcast events, e.g. "Opened") beats a
// raw `name` (custom events get their underscores swapped for spaces).
export const eventItemDisplayName = (item: EventCatalogItem): string =>
  item.label || (item.name ? item.name.replace(/_/g, " ") : item.event_type || "");

// A category is either a flat list of events (e.g. "Custom Events") or,
// like "Broadcast Events" now, a list further split by channel key
// (sms/whatsapp/email/rcs) — same 10-outcome catalog repeated per channel.
export type EventCatalogGroup = EventCatalogItem[] | Record<string, EventCatalogItem[]>;
export const isChannelGroupedEvents = (group: EventCatalogGroup): group is Record<string, EventCatalogItem[]> =>
  !Array.isArray(group);

// GET /segments/events groups (2026-08-06): "Broadcast Events" (channel-
// grouped, see above), "Custom Events" (flat, built-in + org-registered) —
// render each key as a section header with its entries underneath, same
// pattern as AttributeCatalog. The response's separate top-level
// "broadcast_filters" array isn't a category — see BroadcastFilterField.
export type EventCatalog = Record<string, EventCatalogGroup>;

// GET /segments/events' "broadcast_filters" — extra fields that narrow a
// Broadcast Events rule to one specific campaign/name/date via
// EventRule.attributePath (not yet wired into the rule-editing UI).
export interface BroadcastFilterField {
  attribute_path: string;
  label: string;
  type: AttributeFieldType;
  operators: RuleOperator[];
}

// GET /segments/counts response — baseline (no rule_tree param) or filtered
// (with one). Confirmed real shape (2026-08-19): keyed by whichever
// source_scope was actually sent — only ONE scope key comes back per call
// now (previously "all" and "crm" both came back together; that's no longer
// true, and a 4th real scope, "sms_portal", exists too — see ContactScope).
export interface ScopeCounts {
  contacts: number;
  valid_contacts: number;
  invalid_contacts: number;
}
export type SegmentCountsResponse = Partial<Record<ContactScope, ScopeCounts>>;

export type RuleOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "greater_than"
  | "less_than"
  | "between"
  | "before"
  | "after"
  | "is_set"
  | "is_not_set";

// Operators that don't need a value typed in at all.
export const VALUELESS_OPERATORS: RuleOperator[] = ["is_set", "is_not_set"];

// Fallback operator list for a field whose catalog entry isn't known yet
// (e.g. a hydrated rule before the attributes catalog has loaded/matched it).
export const ALL_OPERATORS: { value: RuleOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "not equals" },
  { value: "contains", label: "contains" },
  { value: "greater_than", label: "greater than" },
  { value: "less_than", label: "less than" },
  { value: "between", label: "between" },
  { value: "before", label: "before" },
  { value: "after", label: "after" },
  { value: "is_set", label: "is set" },
  { value: "is_not_set", label: "is not set" },
];

export const OPERATOR_LABELS: Record<RuleOperator, string> = ALL_OPERATORS.reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {} as Record<RuleOperator, string>,
);

export interface AttributeRule {
  id: string;
  kind: "attribute";
  field: string; // the real backend key, e.g. "attributes.wallet_balance"
  label?: string; // display label — from the catalog on fresh pick, humanized on hydration
  type?: AttributeFieldType; // from the catalog — drives which value input(s) render
  operators?: RuleOperator[]; // from the catalog — drives which operators are offered
  options?: (string | AttributeSelectOption)[]; // from the catalog — only for type === "select"
  operator: RuleOperator | "";
  value: string;
  valueTo?: string; // only used when operator === "between"
}

export interface EventRule {
  id: string;
  kind: "event";
  eventType: string;
  occurredInDays?: number; // omit to mean "ever"
  // "Orders >10" / "No Purchase (90+ Days)" style count-threshold rules —
  // omit countOperator entirely for "happened at least once, ever".
  countOperator?: "equals" | "not_equals" | "greater_than" | "less_than" | "";
  countValue?: string;
  // Filters on a key INSIDE the event's own payload (custom events, e.g.
  // "order_value greater_than 100") OR narrows a Broadcast Events condition
  // to one campaign/channel/name/date (attribute_path "campaign_id" |
  // "broadcast_channel" | "broadcast_name" | "broadcast_date") — same
  // attribute_path/operator/value(/value_to) shape either way. All-or-nothing:
  // only meaningful together with attributePath set. This is only ever the
  // ONE filter the "Channel" dropdown in segment-builder.tsx can edit today.
  attributePath?: string;
  eventOperator?: RuleOperator | "";
  eventValue?: string;
  eventValueTo?: string;
  // Any OTHER narrowing filters the backend sent alongside the one above —
  // e.g. the "Add to Segment" flow from the report page returns a
  // campaign_id filter (pin this event to one specific broadcast) together
  // with the broadcast_channel filter above. There's no UI to edit these
  // yet, but they must round-trip untouched through hydrate → save, or a
  // segment created that way silently loses what made it specific (see
  // ruleToTreeNode/rawLeafToRule below) — confirmed via a real
  // /segments/campaign/{id} response, 2026-08-20.
  otherFilters?: { attributePath: string; operator: RuleOperator; value: string; valueTo?: string | null }[];
}

export type SegmentRule = AttributeRule | EventRule;

// Rule groups are OR'd together; rules inside a group are AND'd together.
export interface SegmentRuleGroup {
  id: string;
  rules: SegmentRule[];
}

// ── rule_tree serialization — the shape the "update segment" API expects.
export interface RuleTreeAttributeLeaf {
  type: "attribute";
  field: string;
  operator: RuleOperator;
  value: string | number;
  value_to?: string | number | null;
}

// One narrowing filter inside an event leaf's "filters" array — confirmed
// real shape (2026-08-20), e.g. { attribute_path: "campaign_id", operator:
// "equals", value: "<uuid>", value_to: null }.
export interface RuleTreeFilter {
  attribute_path: string;
  operator: RuleOperator;
  value: string | number;
  value_to?: string | number | null;
}

export interface RuleTreeEventLeaf {
  type: "event";
  event_type: string;
  occurred_in_days?: number;
  count_operator?: "equals" | "not_equals" | "greater_than" | "less_than";
  count_value?: number;
  // Current backend shape: any number of narrowing filters live in this
  // array (campaign_id, broadcast_channel, broadcast_name, broadcast_date —
  // any combination). The old flat attribute_path/operator/value directly on
  // the leaf (kept below) was last confirmed only for the single-filter
  // case and is kept as a fallback for parsing older saved segments, but
  // groupsToRuleTree() only ever emits the "filters" array form now.
  filters?: RuleTreeFilter[];
  attribute_path?: string;
  operator?: RuleOperator;
  value?: string | number;
  value_to?: string | number | null;
}

export interface RuleTree {
  logic: "AND" | "OR";
  children: (RuleTreeAttributeLeaf | RuleTreeEventLeaf | RuleTree)[];
}

export const isRuleFilled = (rule: SegmentRule): boolean => {
  if (rule.kind === "event") return !!rule.eventType;
  if (!rule.field || !rule.operator) return false;
  if (VALUELESS_OPERATORS.includes(rule.operator)) return true;
  if (rule.operator === "between") return !!rule.value && !!rule.valueTo;
  return !!rule.value;
};

const ruleToTreeNode = (rule: SegmentRule): RuleTreeAttributeLeaf | RuleTreeEventLeaf => {
  if (rule.kind === "event") {
    // Combine the one UI-editable narrowing filter (Channel dropdown) with
    // any other filters the backend sent that this UI doesn't edit yet
    // (e.g. campaign_id from the report page's "Add to Segment") — both go
    // into the same "filters" array so nothing gets silently dropped on save.
    const filters: RuleTreeFilter[] = [
      ...(rule.otherFilters || []).map((f) => ({
        attribute_path: f.attributePath,
        operator: f.operator,
        value: f.value,
        ...(f.operator === "between" ? { value_to: f.valueTo ?? null } : {}),
      })),
      ...(rule.attributePath
        ? [
            {
              attribute_path: rule.attributePath,
              operator: (rule.eventOperator || "equals") as RuleOperator,
              value: rule.eventValue as string | number,
              ...(rule.eventOperator === "between" ? { value_to: rule.eventValueTo ?? null } : {}),
            },
          ]
        : []),
    ];
    return {
      type: "event",
      event_type: rule.eventType,
      ...(rule.occurredInDays ? { occurred_in_days: rule.occurredInDays } : {}),
      ...(rule.countOperator
        ? { count_operator: rule.countOperator, count_value: rule.countValue ? Number(rule.countValue) : 0 }
        : {}),
      ...(filters.length > 0 ? { filters } : {}),
    };
  }
  const asNumber = rule.type === "number";
  return {
    type: "attribute",
    field: rule.field,
    operator: (rule.operator || "equals") as RuleOperator,
    value: asNumber ? Number(rule.value) : rule.value,
    value_to: rule.operator === "between" ? (asNumber ? Number(rule.valueTo) : rule.valueTo) ?? null : null,
  };
};

// Groups OR'd together, each group's rules AND'd together — collapses to a
// single flat AND tree when there's only one group, matching the example
// payload exactly; nests as OR-of-ANDs when there's more than one group.
export const groupsToRuleTree = (groups: SegmentRuleGroup[]): RuleTree => {
  const filledGroups = groups
    .map((g) => ({ ...g, rules: g.rules.filter(isRuleFilled) }))
    .filter((g) => g.rules.length > 0);

  if (filledGroups.length <= 1) {
    return { logic: "AND", children: (filledGroups[0]?.rules || []).map(ruleToTreeNode) };
  }
  return {
    logic: "OR",
    children: filledGroups.map((g) => ({ logic: "AND", children: g.rules.map(ruleToTreeNode) })),
  };
};

// Falls back to this for backend attribute keys the loaded catalog hasn't
// matched yet (e.g. right after hydration, before the catalog fetch resolves).
const humanizeAttributeField = (field: string): string => {
  const bare = field.replace(/^attributes\./, "");
  return bare
    .split(/[._]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// A GET /segments/{id} response's rule_tree, and any group node inside it —
// looser than RuleTree/RuleTreeAttributeLeaf since the backend echoes back a
// "type": "group" marker on every group node that groupsToRuleTree() never sends.
interface RawRuleTreeNode {
  type?: string;
  logic?: "AND" | "OR";
  children?: RawRuleTreeNode[];
  field?: string;
  event_type?: string;
  operator?: RuleOperator;
  value?: string | number;
  value_to?: string | number | null;
  occurred_in_days?: number;
  count_operator?: "equals" | "not_equals" | "greater_than" | "less_than";
  count_value?: number;
  attribute_path?: string;
  filters?: RuleTreeFilter[];
}

let hydratedRuleCounter = 0;
const nextHydratedId = (prefix: string) => `${prefix}_hydrated_${Date.now()}_${hydratedRuleCounter++}`;

const rawLeafToRule = (leaf: RawRuleTreeNode): SegmentRule => {
  if (leaf.type === "event" || leaf.event_type) {
    // Current backend shape: narrowing filters live in a "filters" array
    // (any combination of campaign_id/broadcast_channel/broadcast_name/
    // broadcast_date). The "Channel" dropdown only edits the
    // broadcast_channel entry — everything else round-trips untouched via
    // otherFilters (see ruleToTreeNode) instead of being silently dropped.
    // Falls back to the old flat attribute_path/operator/value directly on
    // the leaf for segments saved before "filters" existed.
    const filters = leaf.filters && leaf.filters.length > 0
      ? leaf.filters
      : leaf.attribute_path
      ? [{ attribute_path: leaf.attribute_path, operator: leaf.operator || "equals", value: leaf.value as string | number, value_to: leaf.value_to }]
      : [];
    const channelFilter = filters.find((f) => f.attribute_path === "broadcast_channel");
    const otherFilters = filters
      .filter((f) => f !== channelFilter)
      .map((f) => ({
        attributePath: f.attribute_path,
        operator: f.operator,
        value: f.value != null ? String(f.value) : "",
        valueTo: f.value_to != null ? String(f.value_to) : undefined,
      }));
    return {
      id: nextHydratedId("rule"),
      kind: "event",
      eventType: leaf.event_type || "",
      occurredInDays: leaf.occurred_in_days,
      countOperator: leaf.count_operator || "",
      countValue: leaf.count_value != null ? String(leaf.count_value) : undefined,
      attributePath: channelFilter?.attribute_path,
      eventOperator: channelFilter ? channelFilter.operator || "equals" : "",
      eventValue: channelFilter?.value != null ? String(channelFilter.value) : undefined,
      eventValueTo: channelFilter?.value_to != null ? String(channelFilter.value_to) : undefined,
      ...(otherFilters.length > 0 ? { otherFilters } : {}),
    };
  }
  return {
    id: nextHydratedId("rule"),
    kind: "attribute",
    field: leaf.field || "",
    label: humanizeAttributeField(leaf.field || ""),
    operator: leaf.operator || "equals",
    value: leaf.value != null ? String(leaf.value) : "",
    valueTo: leaf.value_to != null ? String(leaf.value_to) : undefined,
  };
};

// Reverse of groupsToRuleTree() — turns a saved rule_tree back into the
// builder's OR-of-AND-groups shape so an existing segment's rules pre-fill
// when it's opened for editing.
export const ruleTreeToGroups = (tree?: RawRuleTreeNode | null): SegmentRuleGroup[] => {
  if (!tree || !Array.isArray(tree.children) || tree.children.length === 0) return [];

  if (tree.logic === "OR") {
    return tree.children.map((child) => ({
      id: nextHydratedId("group"),
      rules: Array.isArray(child.children) ? child.children.map(rawLeafToRule) : [rawLeafToRule(child)],
    }));
  }

  // Flat AND tree — every child is a leaf (or, defensively, a nested group
  // whose own children get flattened in, since the builder only models two levels).
  const rules: SegmentRule[] = [];
  tree.children.forEach((child) => {
    if (Array.isArray(child.children)) child.children.forEach((leaf) => rules.push(rawLeafToRule(leaf)));
    else rules.push(rawLeafToRule(child));
  });
  return [{ id: nextHydratedId("group"), rules }];
};
